import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '@/components/ui/Dropdown';
import Button from '@/components/ui/Button';
import Radio from '@/components/ui/Radio/Radio';
import icFilterExpander from '@/assets/icons/filterExpander.svg';
import icCustomRangeExpander from '@/assets/icons/customRangeExpander.svg';
import icCustomRangeExpanderActive from '@/assets/icons/customRangeExpander-active.svg';
import icArrowRight from '@/assets/icons/arrowRight.svg';
import styles from './PageSizeSelector.module.css';

interface PageSizeSelectorProps {
  value: number;
  onChange: (value: number) => void;
}

const PRESET_OPTIONS = [10, 50, 100, 200, 1000];
const MAX_CUSTOM_VALUE = 100000;

export const PageSizeSelector = ({ value, onChange }: PageSizeSelectorProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState<string>('');

  const isPresetOption = PRESET_OPTIONS.includes(value);
  const isCustomSelected = !isPresetOption;

  const handlePresetSelect = (newValue: number) => {
    onChange(newValue);
    setShowCustomInput(false);
    setIsOpen(false);
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    setCustomValue(isCustomSelected ? value.toString() : '');
  };

  const handleCustomSubmit = () => {
    const numValue = parseInt(customValue, 10);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= MAX_CUSTOM_VALUE) {
      onChange(numValue);
      setShowCustomInput(false);
      setIsOpen(false);
    }
  };

  const handleCustomKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomSubmit();
    }
  };

  const getDisplayLabel = () => {
    if (isPresetOption) {
      return value.toString();
    }
    return `${value} (${t('customPageSize')})`;
  };

  const renderOptionsContent = () => (
    <>
      {PRESET_OPTIONS.map((option) => (
        <label
          key={option}
          className={`${styles.filterOption} ${value === option ? styles.filterOptionChecked : ''}`}
        >
          <Radio checked={value === option} onChange={() => handlePresetSelect(option)} />
          <span className={`${value === option ? 'body-md-semibold' : 'body-md-regular'} text-(--font-body-1)`}>
            {option}
          </span>
        </label>
      ))}
      <div
        className={`${styles.customRangeOption} ${isCustomSelected ? 'bg-(--surface-secondary-lowest)' : ''}`}
        onClick={handleCustomClick}
      >
        <span
          className={`${styles.customRangeText} ${
            isCustomSelected ? 'body-md-medium text-(--font-body-5)' : 'body-md-semibold text-(--font-body-1)'
          }`}
        >
          {isCustomSelected ? value : t('customPageSize')}
        </span>
        <img src={isCustomSelected ? icCustomRangeExpanderActive : icCustomRangeExpander} alt="arrow" />
      </div>
    </>
  );

  const renderCustomInputContent = () => (
    <div className={styles.rangeFilter}>
      <div className={styles.backToOptions}>
        <img
          src={icArrowRight}
          className="cursor-pointer"
          onClick={() => setShowCustomInput(false)}
          alt="back"
        />
      </div>
      <div className={styles.rangeContent}>
        <div className={styles.rangeInputsGroup}>
          <input
            type="number"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyPress={handleCustomKeyPress}
            placeholder={`1-${MAX_CUSTOM_VALUE}`}
            min={1}
            max={MAX_CUSTOM_VALUE}
            className={styles.customInput}
            autoFocus
          />
        </div>
        <Button
          className={`${styles.rangeApplyButton} body-md-semibold text-(--font-body-4)`}
          onClick={handleCustomSubmit}
          disabled={!customValue || parseInt(customValue) < 1 || parseInt(customValue) > MAX_CUSTOM_VALUE}
        >
          {t('apply')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <Dropdown
        trigger={
          <Button
            variant="white"
            className={`${styles.filterButton} ${isOpen ? styles.filterButtonDropdownOpen : ''} ${
              value ? styles.filterButtonHasValue : ''
            }`}
          >
            <span className="body-md-regular text-(--font-primary)">{getDisplayLabel()}</span>
            <img src={icFilterExpander} alt="expander" />
          </Button>
        }
        contentAlign="start"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        <div className={styles.filterDropdown}>
          {showCustomInput ? renderCustomInputContent() : renderOptionsContent()}
        </div>
      </Dropdown>
    </div>
  );
};

export default PageSizeSelector;
